import { resetPassword, confirmResetPassword } from "@aws-amplify/auth";
import { Form, Input, Typography, Button as AntButton } from "antd";
import { ReactComponent as Logo } from "shared/assets/images/logo-strm.svg";
import { ReactComponent as IconCheck } from "shared/assets/images/icon-check.svg";
import { LanguageChange } from "shared/components";
import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { RouteComponentProps } from "react-router";
import { isMobile } from "react-device-detect";
import { toast } from "react-toastify";
import LoadingButton from "shared/components/button/LoadingButton";

interface ResetPasswordProps extends RouteComponentProps {}

interface AmplifyError {
  name: string;
  message: string;
}

interface FormValidationError {
  errorFields: Array<{
    name: (string | number)[];
    errors: string[];
  }>;
  outOfDate: boolean;
  values: any;
}

type ResetPasswordStep = "first" | "second";

const ResetSenha: React.FC<ResetPasswordProps> = ({ history }) => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const [step, setStep] = useState<ResetPasswordStep>("first");
  const [loading, setLoading] = useState<boolean>(false);

  const password = Form.useWatch("password", form) || "";

  const passwordRules = useMemo(() => {
    return [
      {
        label: t("pages.CadastreSe.umaLetraMaiuscula"),
        regex: /[A-Z]/,
      },
      {
        label: t("pages.CadastreSe.umCaractereEspecial"),
        regex: /[^A-Za-z0-9]/,
      },
      {
        label: t("pages.CadastreSe.umNumero"),
        regex: /[0-9]/,
      },
      {
        label: t("pages.CadastreSe.oitoCaracteres"),
        regex: /.{8,}/,
      },
    ];
  }, [t]);

  const handleResetError = (error: AmplifyError, t: TFunction): void => {
    const errorMappings: Record<string, string> = {
      CodeMismatchException: t("pages.ResetSenha.codigoInvalido"),
      ExpiredCodeException: t("pages.ResetSenha.errors.expiredCode"),
      NotAuthorizedException: t("pages.ResetSenha.errors.notAuthorized"),
      LimitExceededException: t("pages.ResetSenha.limiteExcedido"),
      TooManyRequestsException: t("pages.ResetSenha.limiteExcedido"),
      UserNotFoundException: t("pages.ResetSenha.errors.userNotFound"),
      InvalidPasswordException: t("pages.ResetSenha.errors.invalidPassword"),
      InvalidParameterException: t("pages.ResetSenha.errors.invalidParameter"),
      NetworkError: t("pages.ResetSenha.errors.networkError"),
      ServiceException: t("pages.ResetSenha.errors.serviceError"),
      InternalErrorException: t("pages.ResetSenha.errors.internalError"),
    };

    const errorMessage = errorMappings[error.name] || error.message;
    toast.error(errorMessage);
  };

  const handleFormValidationError = (
    error: FormValidationError,
    t: TFunction
  ): void => {
    if (error.errorFields?.length > 0) {
      const firstError = error.errorFields[0];
      const errorMessage = firstError.errors[0];
      toast.error(errorMessage);
    }
  };

  const handleFirstStepSubmit = async () => {
    setLoading(true);

    try {
      const values = await form.validateFields(["email"]);
      await resetPassword({ username: values.email });
      setStep("second");
    } catch (error: any) {
      if (error.errorFields) {
        handleFormValidationError(error, t);
      } else {
        handleResetError(error, t);
      }
    } finally {
      setLoading(false);
    }
  };

  const validatePasswordCriteria = (): boolean => {
    const hasInvalidCriteria = passwordRules.some(
      (rule) => !rule.regex.test(password)
    );
    if (hasInvalidCriteria || !password) {
      toast.error<string>(t("pages.ResetSenha.errors.passwordCriteriaNotMet"));
      return false;
    }
    return true;
  };

  const handleSecondStepSubmit = async () => {
    if (!validatePasswordCriteria()) {
      return;
    }

    setLoading(true);

    try {
      const values = await form.validateFields([
        "codigo",
        "password",
        "confirmacaoSenha",
      ]);
      await confirmResetPassword({
        username: form.getFieldValue("email"),
        confirmationCode: values.codigo,
        newPassword: values.password,
      });

      toast.success<string>(t("pages.ResetSenha.sucesso"));
      history.push("/login");
    } catch (error: any) {
      if (error.errorFields) {
        handleFormValidationError(error, t);
      } else {
        handleResetError(error, t);
      }
    } finally {
      setLoading(false);
    }
  };

  const passwordRulesStyle = {
    marginTop: "8px",
    marginBottom: "16px",
  };

  const ruleStyle = {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    marginBottom: "4px",
    fontSize: "14px",
    color: "hsl(0, 0%, 60%)",
  };

  const ruleValidStyle = {
    ...ruleStyle,
    color: "hsl(156, 100%, 50%)",
  };

  const checkIconStyle = {
    width: "15px",
    height: "11px",
    marginRight: "6px",
  };

  const checkIconValidStyle = {
    ...checkIconStyle,
    stroke: "hsl(156, 100%, 50%)",
  };

  const checkIconInvalidStyle = {
    ...checkIconStyle,
    stroke: "hsl(0, 0%, 60%)",
  };

  return (
    <div
      className="conteudo-centralizado login"
      style={{
        height: "100vh",
        backgroundColor: "hsl(300, 2%, 10%)",
        padding: "14px 26px",
      }}
    >
      <div className="direction">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <div className="logo-region">
            <Logo className="logo" style={{ width: isMobile ? 80 : 100 }} />
            <span style={{ fontSize: isMobile ? 14 : 28 }}>for artists</span>
          </div>
          <LanguageChange.SelectLanguageChange />
        </div>

        <Form form={form} name="resetPasswordForm">
          {step === "first" && (
            <div className="content" style={{ marginTop: 154 }}>
              <div className="text">
                <Typography.Title
                  style={{ color: "hsl(0, 0%, 95%)", fontSize: 20 }}
                >
                  {t("pages.ResetSenha.titulo")}
                </Typography.Title>
                <Typography.Paragraph
                  style={{
                    color: "hsl(0, 0%, 95%)",
                    fontSize: 13,
                    marginTop: ".5rem",
                    marginBottom: 0,
                  }}
                >
                  {t("pages.ResetSenha.avisoLoginExterno1")}
                </Typography.Paragraph>
                <Typography.Paragraph
                  style={{
                    color: "hsl(0, 0%, 95%)",
                    fontSize: 13,
                  }}
                >
                  {t("pages.ResetSenha.avisoLoginExterno2")}
                </Typography.Paragraph>
              </div>
              <div className="input">
                <Form.Item
                  name="email"
                  rules={[
                    {
                      required: true,
                      message: t("pages.ResetSenha.emailRequired"),
                    },
                  ]}
                >
                  <Input
                    type="email"
                    placeholder={t("pages.ResetSenha.email")}
                  />
                </Form.Item>
              </div>

              <div className="enter-button">
                <LoadingButton
                  className="botao-roxo"
                  loading={loading}
                  onClick={handleFirstStepSubmit}
                  type="button"
                  style={{ cursor: "pointer" }}
                >
                  {t("pages.ResetSenha.submit")}
                </LoadingButton>
              </div>
            </div>
          )}

          {step === "second" && (
            <div className="content" style={{ marginTop: 154 }}>
              <div className="text">
                <Typography.Title
                  style={{ color: "hsl(0, 0%, 95%)", fontSize: 20 }}
                >
                  {t("pages.ResetSenha.titulo")}
                </Typography.Title>
              </div>
              <div className="input">
                <Form.Item
                  name="codigo"
                  rules={[
                    {
                      required: true,
                      message: t("pages.ResetSenha.codigoRequired"),
                    },
                  ]}
                  normalize={(value) => value?.trim()}
                >
                  <Input placeholder={t("pages.ResetSenha.codigo")} />
                </Form.Item>
                <Form.Item
                  name="password"
                  rules={[
                    {
                      required: true,
                      message: t("pages.ResetSenha.senhaRequired"),
                    },
                  ]}
                  style={{ marginBottom: 0 }}
                >
                  <Input.Password placeholder={t("pages.ResetSenha.senha")} />
                </Form.Item>
                <div style={passwordRulesStyle}>
                  {passwordRules.map((rule, index) => {
                    const isValid = rule.regex.test(password);
                    return (
                      <div
                        key={index}
                        style={isValid ? ruleValidStyle : ruleStyle}
                      >
                        <IconCheck
                          style={
                            isValid
                              ? checkIconValidStyle
                              : checkIconInvalidStyle
                          }
                        />
                        <span>{rule.label}</span>
                      </div>
                    );
                  })}
                </div>
                <Form.Item
                  name="confirmacaoSenha"
                  dependencies={["password"]}
                  rules={[
                    {
                      required: true,
                      message: t("pages.ResetSenha.confirmacaoSenhaRequired"),
                    },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue("password") === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(
                          new Error(t("pages.ResetSenha.senhaNaoCombina"))
                        );
                      },
                    }),
                  ]}
                >
                  <Input.Password
                    placeholder={t("pages.ResetSenha.confirmacaoSenha")}
                  />
                </Form.Item>
              </div>

              <div className="enter-button">
                <LoadingButton
                  className="botao-roxo"
                  loading={loading}
                  onClick={handleSecondStepSubmit}
                  type="button"
                  disabled={
                    passwordRules.some((rule) => !rule.regex.test(password)) ||
                    !password
                  }
                  style={{ cursor: "pointer" }}
                >
                  {t("pages.ResetSenha.submit")}
                </LoadingButton>
              </div>
            </div>
          )}
        </Form>

        <div className="esqueci-senha">
          <AntButton
            type="link"
            htmlType="button"
            onClick={() => history.goBack()}
            className="button-reset-senha"
            style={{ cursor: "pointer" }}
          >
            {t("pages.ResetSenha.voltar")}
          </AntButton>
        </div>
      </div>
    </div>
  );
};

export default ResetSenha;
